import React, { useState, useMemo } from 'react';
import { Wand2, ChevronDown, ChevronRight } from 'lucide-react';
import SVGColorBlock from '../SVGColorBlock';
import SceneColorPicker from './SceneColorPicker';
import {
	EXPERT_CATEGORIES,
	getExpertMethod,
	getExpertCategoryGroup,
	findExpertGroupIdForMethod,
	runExpertMethod,
} from '../colorExpertUtils';
import {
	buildZoneRotationCycle,
	shouldOfferZoneRotationCycle,
	getZoneRotationCycleStepCount,
} from '../lookColorUtils';
import { MIN_CYCLE_HOLD_SECONDS } from '../sceneUtils';

function ExpertColorPanel({
	numColors,
	targetLookIndex,
	lookCount = 1,
	isCycle,
	onApplyLook,
	onApplyNewLook,
	onApplyCycle,
	onApplyAllLooks,
}) {
	const [open, setOpen] = useState(false);
	const [categoryId, setCategoryId] = useState('color_theory');
	const [groupId, setGroupId] = useState(null);
	const [methodId, setMethodId] = useState('complementary');
	const [baseColor, setBaseColor] = useState({ r: 128, g: 128, b: 128 });
	const [applyMode, setApplyMode] = useState('current_look');

	const category = EXPERT_CATEGORIES.find((c) => c.id === categoryId) || EXPERT_CATEGORIES[0];
	const groups = category.groups || [];
	const hasGroups = groups.length > 0;
	const activeGroupId = hasGroups
		? (groups.find((g) => g.id === groupId)?.id || groups[0]?.id)
		: null;
	const activeGroup = hasGroups ? getExpertCategoryGroup(categoryId, activeGroupId) : null;
	const groupMethods = hasGroups ? (activeGroup?.methods || []) : (category.methods || []);
	const method = getExpertMethod(methodId);
	const preview = useMemo(
		() => runExpertMethod(methodId, baseColor, numColors),
		[methodId, baseColor, numColors]
	);

	const handleCategoryChange = (id) => {
		setCategoryId(id);
		const cat = EXPERT_CATEGORIES.find((c) => c.id === id);
		if (cat?.groups?.[0]) {
			const firstGroup = cat.groups[0];
			setGroupId(firstGroup.id);
			setMethodId(firstGroup.methods[0]?.id || methodId);
		} else if (cat?.methods?.[0]) {
			setGroupId(null);
			setMethodId(cat.methods[0].id);
		}
	};

	const handleGroupChange = (id) => {
		setGroupId(id);
		const group = getExpertCategoryGroup(categoryId, id);
		if (group?.methods?.[0]) {
			setMethodId(group.methods[0].id);
		}
	};

	const canCycleApply = preview?.type === 'cycle' || method?.cycle;
	const canZoneRotationCycle = shouldOfferZoneRotationCycle(numColors, preview);
	const zoneCycleSteps = canZoneRotationCycle ? getZoneRotationCycleStepCount(preview) : 0;
	const selectClass = 'w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 rounded-lg px-2 py-1.5 text-xs';

	const handleApply = () => {
		const result = runExpertMethod(methodId, baseColor, numColors);
		if (!result) return;

		if (applyMode === 'zone_rotation_cycle' && result.type === 'look') {
			const cycle = buildZoneRotationCycle(result.colors, numColors, MIN_CYCLE_HOLD_SECONDS);
			if (cycle?.looks) {
				onApplyCycle(cycle.looks);
			}
			return;
		}

		if (applyMode === 'cycle_scene' && result.type === 'cycle') {
			onApplyCycle(result.looks);
			return;
		}

		const colors = result.type === 'cycle' ? result.looks[0].colors : result.colors;

		if (applyMode === 'all_looks_same') {
			onApplyAllLooks(colors);
			return;
		}

		if (applyMode === 'new_look') {
			onApplyNewLook(colors);
			return;
		}

		onApplyLook(targetLookIndex, colors);
	};

	return (
		<div className="border border-dashed border-[#880e4f]/40 rounded-xl overflow-hidden bg-[#880e4f]/5 dark:bg-[#880e4f]/10">
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#880e4f]/10 transition-colors"
			>
				{open ? <ChevronDown size={16} className="text-[#880e4f]" /> : <ChevronRight size={16} className="text-[#880e4f]" />}
				<Wand2 size={16} className="text-[#880e4f]" />
				<span className="text-sm font-medium text-[#880e4f]">Expert Color Generator</span>
			</button>

			{open && (
				<div className="px-3 pb-3 pt-1 space-y-3 border-t border-[#880e4f]/20">
					<p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
						Pick a source color, choose a method, then apply to the current look, a new look, or generate a full cycle.
					</p>

					<div>
						<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Source Color</label>
						<SceneColorPicker
							color={baseColor}
							onChange={setBaseColor}
							label="Expert source"
							size={36}
							zIndex={80}
						/>
					</div>

					<div>
						<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Category</label>
						<select
							className={selectClass}
							value={categoryId}
							onChange={(e) => handleCategoryChange(e.target.value)}
						>
							{EXPERT_CATEGORIES.map((c) => (
								<option key={c.id} value={c.id}>{c.label}</option>
							))}
						</select>
					</div>

					{hasGroups && (
						<div>
							<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">League</label>
							<select
								className={selectClass}
								value={activeGroupId || ''}
								onChange={(e) => handleGroupChange(e.target.value)}
							>
								{groups.map((group) => (
									<option key={group.id} value={group.id}>{group.label}</option>
								))}
							</select>
						</div>
					)}

					<div>
						<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
							{hasGroups ? 'Team / Preset' : 'Method'}
						</label>
						<select
							className={selectClass}
							value={methodId}
							onChange={(e) => {
								setMethodId(e.target.value);
								if (hasGroups) {
									const nextGroupId = findExpertGroupIdForMethod(categoryId, e.target.value);
									if (nextGroupId) setGroupId(nextGroupId);
								}
							}}
						>
							{groupMethods.map((m) => (
								<option key={m.id} value={m.id}>{m.label}</option>
							))}
						</select>
						{method?.description && (
							<p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
								{method.region ? `${method.region} — ` : ''}{method.description}
							</p>
						)}
					</div>

					{preview && (
						<div>
							<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Preview</label>
							{preview.type === 'cycle' ? (
								<div className="space-y-1">
									{preview.looks.map((look, i) => (
										<div key={i} className="flex items-center gap-2">
											<span className="text-[10px] text-gray-400 w-12 shrink-0">Look {i + 1}</span>
											<SVGColorBlock size={22} data={look.colors} />
										</div>
									))}
								</div>
							) : (
								<SVGColorBlock size={28} data={preview.colors} />
							)}
						</div>
					)}

					{canZoneRotationCycle && (
						<p className="text-[10px] text-[#880e4f]/80 dark:text-[#880e4f] leading-snug">
							{numColors === 1
								? 'Single zone: apply as a rotation cycle to show each expert color in sequence.'
								: `${numColors} zones: apply as a rotation cycle to chase colors across fixtures.`}
						</p>
					)}

					<div>
						<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Apply To</label>
						<select
							className={selectClass}
							value={applyMode}
							onChange={(e) => setApplyMode(e.target.value)}
						>
							<option value="current_look">
								Current look{isCycle ? ` (Look ${targetLookIndex + 1})` : ''}
							</option>
							<option value="new_look">
								New look (Look {lookCount + 1})
							</option>
							{canZoneRotationCycle && (
								<option value="zone_rotation_cycle">
									Zone rotation cycle ({zoneCycleSteps} looks{numColors === 1 ? ', one color each' : ''})
								</option>
							)}
							{canCycleApply && (
								<option value="cycle_scene">Full cycle scene ({preview?.looks?.length || 'multi'}-look sequence)</option>
							)}
							{isCycle && (
								<option value="all_looks_same">All looks (same palette)</option>
							)}
						</select>
					</div>

					<button
						type="button"
						onClick={handleApply}
						className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm bg-[#880e4f] hover:bg-[#6b0b3f] text-white rounded-xl font-medium transition-colors"
					>
						<Wand2 size={15} /> Apply Expert Palette
					</button>
				</div>
			)}
		</div>
	);
}

export default ExpertColorPanel;
